import type { FunctionComponent, SVGProps } from 'react';

import {
  FaBuilding,
  FaChartBar,
  FaFileContract,
  FaFileSignature,
  FaRegFileAlt,
  FaTag,
  FaUserCircle
} from 'react-icons/fa';

import { BarnIcon, ProducerIcon, StakeholderIcon } from '@/design-system/icons';

import { type AuthStateType } from '@/shared/authentication/utils/constants';
import { isAuthorized, type AllowEntry } from '@/shared/authorization';
import { ADMINS, GLOBAL_ADMIN } from '@/shared/authorization/permissionGroups';

import type { IconType } from 'react-icons';

export interface AdminSection {
  slug: string;
  label: string;
  icon: IconType | FunctionComponent<SVGProps<SVGSVGElement>>;
  search: string;
  hideSearch?: boolean;
  /** Absent gate = visible to every role that may enter the admin module. */
  gate?: readonly AllowEntry[];
}

// Order is load-bearing: it is the legacy left-menu order, and `/admin?id=N`
// bookmarks index into this list after role filtering (see admin-routes.tsx).
export const ADMIN_SECTIONS = [
  {
    slug: 'insurance-agencies',
    label: 'Insurance Agencies',
    icon: FaBuilding,
    search: 'agencies',
    gate: GLOBAL_ADMIN
  },
  {
    slug: 'insurance-agents',
    label: 'Insurance Agents',
    icon: FaUserCircle,
    search: 'agents',
    hideSearch: true,
    gate: ADMINS
  },
  {
    slug: 'operations',
    label: 'Operations',
    icon: BarnIcon as IconType,
    search: 'operations',
    gate: undefined
  },
  {
    slug: 'producers',
    label: 'Producers',
    icon: ProducerIcon as IconType,
    search: 'producers',
    gate: undefined
  },
  {
    slug: 'stakeholders',
    label: 'Stakeholders',
    icon: StakeholderIcon as IconType,
    search: 'stakeholders',
    gate: undefined
  },
  {
    slug: 'tag-types',
    label: 'Tag Types',
    icon: FaTag,
    search: 'tags',
    hideSearch: true,
    gate: ADMINS
  },
  {
    slug: 'agency-licenses',
    label: 'Agency Licenses',
    icon: FaFileContract,
    search: 'licenses',
    hideSearch: true,
    gate: ADMINS
  },
  {
    slug: 'user-agreements',
    label: 'User Agreements',
    icon: FaFileSignature,
    search: 'user-agreements',
    hideSearch: true,
    gate: ADMINS
  },
  {
    slug: 'aip-binding',
    label: 'AIP Binding',
    icon: FaChartBar,
    search: 'aipBinding',
    hideSearch: true,
    gate: GLOBAL_ADMIN
  },
  {
    slug: 'price-reports',
    label: 'Price Reports',
    icon: FaRegFileAlt,
    search: 'price-reports',
    hideSearch: true,
    gate: GLOBAL_ADMIN
  }
] as const satisfies readonly AdminSection[];

export type AdminSectionSlug = (typeof ADMIN_SECTIONS)[number]['slug'];

export const visibleAdminSections = (role: AuthStateType): AdminSection[] =>
  ADMIN_SECTIONS.filter(section => !section.gate || isAuthorized(role, section.gate));
